import React, { Fragment, useContext, useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, Button, Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Input, Badge } from 'reactstrap';
import { PlusCircle, Edit, Trash2, Image as ImageIcon, ToggleLeft, ToggleRight, X, Layers } from 'react-feather';
import { Breadcrumbs } from '../../../../AbstractElements';
import CustomizerContext from '../../../../_helper/Customizer';
import { fetchLoadingImageMiniGameQLT, fetchUploadImageMiniGameQLT, fetchEditImageMiniGameQLT, fetchDeleteImageMiniGameQLT, fetchToggleStatusImageMiniGame } from '../../../../api/image';
import { toast } from 'react-toastify';

const ImageBackgroundManagement = () => {
  const { layoutURL } = useContext(CustomizerContext);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');

  // Load images khi component mount
  useEffect(() => {
    loadImages();
  }, []);

  // Cleanup preview URL
  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Hàm load danh sách image
  const loadImages = async () => {
    setLoading(true);
    try {
      const data = await fetchLoadingImageMiniGameQLT();
      setImages(data);
      toast.success('✅ Tải danh sách image background thành công!');
    } catch (error) {
      toast.error('❌ Lỗi khi tải image: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Xử lý chọn file
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('⚠️ Vui lòng chọn file ảnh!');
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('⚠️ File ảnh quá lớn! Vui lòng chọn file nhỏ hơn 10MB.');
        return;
      }

      setSelectedFile(file);
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
    }
  };

  // Mở modal thêm mới
  const handleAddNew = () => {
    setEditMode(false);
    setCurrentImage(null);
    setSelectedFile(null);
    setPreviewUrl('');
    setModalOpen(true);
  };

  // Mở modal chỉnh sửa
  const handleEdit = (image) => {
    setEditMode(true);
    setCurrentImage(image);
    setPreviewUrl(image.url);
    setSelectedFile(null);
    setModalOpen(true);
  };

  // Xử lý upload/edit image
  const handleSubmit = async () => {
    if (!selectedFile && !editMode) {
      toast.warning('⚠️ Vui lòng chọn file ảnh!');
      return;
    }

    setLoading(true);
    try {
      if (editMode && currentImage) {
        // Edit image
        const formData = new FormData();
        if (selectedFile) {
          formData.append('url', selectedFile);
        }
        await fetchEditImageMiniGameQLT(currentImage._id, formData);
        toast.success('✅ Cập nhật image background thành công!');
      } else {
        // Upload new image
        await fetchUploadImageMiniGameQLT(selectedFile);
        toast.success('✅ Thêm image background mới thành công!');
      }
      
      setModalOpen(false);
      loadImages();
    } catch (error) {
      toast.error('❌ Lỗi: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Xử lý xóa image
  const handleDelete = async (id) => {
    if (window.confirm('⚠️ Bạn có chắc chắn muốn xóa image này?')) {
      setLoading(true);
      try {
        await fetchDeleteImageMiniGameQLT(id);
        toast.success('✅ Xóa image background thành công!');
        loadImages();
      } catch (error) {
        toast.error('❌ Lỗi khi xóa: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  // Xử lý toggle status
  const handleToggleStatus = async (id, currentStatus) => {
    setLoading(true);
    try {
      await fetchToggleStatusImageMiniGame(id);
      const newStatus = !currentStatus;
      toast.success(`✅ ${newStatus ? 'Kích hoạt' : 'Vô hiệu hóa'} image thành công!`);
      loadImages();
    } catch (error) {
      toast.error('❌ Lỗi khi thay đổi trạng thái: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
  };

  // Count active/inactive images
  const activeCount = images.filter(img => img.status === true || img.status === 'active').length;
  const inactiveCount = images.length - activeCount;

  return (
    <Fragment>
      <Breadcrumbs parent="Image Background" title="Background Images" mainTitle="Quản lý Image Background" />
      <Container fluid={true}>
        <Row>
          <Col md="12">
            {/* Info Banner */}
            <Card className="bg-gradient-primary text-white mb-4 border-0 shadow">
              <CardBody className="p-4">
                <div className="d-flex align-items-center">
                  <div className="bg-white bg-opacity-25 rounded-circle p-3 me-3">
                    <Layers size={32} />
                  </div>
                  <div>
                    <h4 className="text-white mb-1">Image Background MiniGame QLT</h4>
                    <p className="mb-0 opacity-75">
                      Quản lý hình ảnh nền cho minigame Quản lý tài sản
                    </p>
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* Statistics Cards */}
            {!loading && images.length > 0 && (
              <Row className="mb-4">
                <Col md="3">
                  <Card className="border-0 shadow-sm">
                    <CardBody className="text-center">
                      <h2 className="text-primary mb-2">{images.length}</h2>
                      <p className="text-muted mb-0">Tổng hình ảnh</p>
                    </CardBody>
                  </Card>
                </Col>
                <Col md="3">
                  <Card className="border-0 shadow-sm">
                    <CardBody className="text-center">
                      <h2 className="text-success mb-2">{activeCount}</h2>
                      <p className="text-muted mb-0">Đang hoạt động</p>
                    </CardBody>
                  </Card>
                </Col>
                <Col md="3">
                  <Card className="border-0 shadow-sm">
                    <CardBody className="text-center">
                      <h2 className="text-danger mb-2">{inactiveCount}</h2>
                      <p className="text-muted mb-0">Đã tắt</p>
                    </CardBody>
                  </Card>
                </Col>
                <Col md="3">
                  <Card className="border-0 shadow-sm">
                    <CardBody className="text-center">
                      <h2 className="text-info mb-2">16:9</h2>
                      <p className="text-muted mb-0">Tỷ lệ khuyến nghị</p>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Main Content Card */}
            <Card className="shadow-sm border-0">
              <CardBody className="p-4">
                {/* Header Section */}
                <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom">
                  <div>
                    <h4 className="mb-1">
                      <ImageIcon size={24} className="me-2 text-primary" />
                      Danh sách Image Background
                    </h4>
                    <p className="text-muted mb-0 small">
                      Quản lý hình ảnh nền cho minigame
                    </p>
                  </div>
                  <Button color="primary" onClick={handleAddNew} className="shadow-sm">
                    <PlusCircle size={16} className="me-2" />
                    Thêm Image Mới
                  </Button>
                </div>

                {/* Loading State */}
                {loading && (
                  <div className="text-center my-5 py-5">
                    <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <h5 className="text-muted">Đang tải dữ liệu...</h5>
                    <p className="text-muted small">Vui lòng chờ trong giây lát</p>
                  </div>
                )}

                {/* Empty State */}
                {!loading && images.length === 0 && (
                  <div className="text-center my-5 py-5">
                    <div className="mb-4">
                      <Layers size={100} className="text-muted opacity-50" />
                    </div>
                    <h4 className="text-muted mb-2">Chưa có image background nào</h4>
                    <p className="text-muted mb-4">
                      Thêm hình ảnh nền đầu tiên cho minigame
                    </p>
                    <Button color="primary" size="lg" onClick={handleAddNew}>
                      <PlusCircle size={18} className="me-2" />
                      Thêm Image Đầu Tiên
                    </Button>
                  </div>
                )}

                {/* Images Grid */}
                {!loading && images.length > 0 && (
                  <Row>
                    {images.map((image, index) => {
                      const isActive = image.status === true || image.status === 'active';
                      
                      return (
                        <Col xl="4" lg="6" md="6" sm="12" key={image._id || index} className="mb-4">
                          <Card className={`shadow-sm border h-100 hover-lift ${!isActive ? 'opacity-75' : ''}`}>
                            {/* Image Container */}
                            <div className="position-relative overflow-hidden" style={{ paddingTop: '56.25%', backgroundColor: '#f8f9fa' }}>
                              <img
                                src={image.url}
                                alt={`Background ${index + 1}`}
                                className="position-absolute top-0 start-0 w-100 h-100 hover-zoom"
                                style={{ 
                                  objectFit: 'cover',
                                  filter: !isActive ? 'grayscale(100%)' : 'none'
                                }}
                                onError={(e) => {
                                  e.target.src = 'https://via.placeholder.com/800x450?text=Background+Image';
                                }}
                              />
                              {/* Status Badge */}
                              <div className="position-absolute top-0 end-0 m-2">
                                <Badge color={isActive ? 'success' : 'danger'} className="px-3 py-2">
                                  {isActive ? '✓ Active' : '✗ Inactive'}
                                </Badge>
                              </div>
                              {/* Index Badge */}
                              <div className="position-absolute top-0 start-0 m-2">
                                <Badge color="primary" className="px-3 py-2">
                                  #{index + 1}
                                </Badge>
                              </div>
                            </div>

                            {/* Card Body */}
                            <CardBody className="bg-light">
                              <div className="mb-2">
                                <p className="mb-1 small text-muted">
                                  <strong>ID:</strong> {image._id?.slice(-8)}
                                </p>
                                <p className="mb-0 small">
                                  <strong>Trạng thái:</strong>{' '}
                                  <span className={isActive ? 'text-success' : 'text-danger'}>
                                    {isActive ? 'Đang hoạt động' : 'Đã tắt'}
                                  </span>
                                </p>
                              </div>
                              
                              {/* Action Buttons */}
                              <div className="d-flex gap-1 flex-wrap">
                                <Button
                                  color={isActive ? 'success' : 'secondary'}
                                  size="sm"
                                  onClick={() => handleToggleStatus(image._id, isActive)}
                                  title={isActive ? 'Vô hiệu hóa' : 'Kích hoạt'}
                                  className="flex-grow-1"
                                >
                                  {isActive ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                                  <span className="ms-1 small">
                                    {isActive ? 'Tắt' : 'Bật'}
                                  </span>
                                </Button>
                                <Button
                                  color="info"
                                  size="sm"
                                  onClick={() => handleEdit(image)}
                                  title="Chỉnh sửa"
                                >
                                  <Edit size={14} />
                                </Button>
                                <Button
                                  color="danger"
                                  size="sm"
                                  onClick={() => handleDelete(image._id)}
                                  title="Xóa"
                                >
                                  <Trash2 size={14} />
                                </Button>
                              </div>
                            </CardBody>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Modal Upload/Edit Image */}
        <Modal isOpen={modalOpen} toggle={handleCloseModal} size="lg" centered>
          <ModalHeader toggle={handleCloseModal} className="bg-light">
            <div className="d-flex align-items-center">
              <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-3">
                {editMode ? <Edit size={20} className="text-primary" /> : <PlusCircle size={20} className="text-primary" />}
              </div>
              <div>
                <h5 className="mb-0">
                  {editMode ? '✏️ Chỉnh sửa Image Background' : '➕ Thêm Image Background Mới'}
                </h5>
                <small className="text-muted">
                  {editMode ? 'Cập nhật hình ảnh nền' : 'Upload hình ảnh nền cho minigame'}
                </small>
              </div>
            </div>
          </ModalHeader>
          <ModalBody className="p-4">
            <FormGroup>
              <Label for="backgroundImage" className="fw-bold fs-6 mb-3">
                <ImageIcon size={18} className="me-2 text-primary" />
                Chọn hình ảnh nền <span className="text-danger">*</span>
              </Label>
              <Input
                id="backgroundImage"
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="form-control-lg"
              />
              <div className="mt-3 p-3 bg-light rounded">
                <small className="text-muted d-block mb-2">
                  <strong>📌 Yêu cầu cho hình ảnh nền:</strong>
                </small>
                <ul className="small text-muted mb-0 ps-3">
                  <li>Định dạng: JPG, PNG, WebP</li>
                  <li>Kích thước tối đa: 10MB</li>
                  <li>Tỷ lệ khuyến nghị: 16:9 (1920x1080px hoặc cao hơn)</li>
                  <li>Chất lượng cao, rõ nét</li>
                  <li>Màu sắc phù hợp với theme minigame</li>
                  <li>Không chứa text hoặc logo che khuất</li>
                </ul>
              </div>
            </FormGroup>

            {/* Preview Section */}
            {previewUrl && (
              <div className="mt-4">
                <Label className="fw-bold fs-6 mb-3">
                  🖼️ Xem trước Background:
                </Label>
                <div className="border rounded overflow-hidden shadow-sm">
                  <div className="position-relative" style={{ paddingTop: '56.25%', backgroundColor: '#f8f9fa' }}>
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="position-absolute top-0 start-0 w-100 h-100"
                      style={{ objectFit: 'cover' }}
                    />
                  </div>
                  <div className="bg-light p-3 text-center border-top">
                    <Badge color="primary" className="px-3 py-2 me-2">
                      <Layers size={12} className="me-1" />
                      Background Image
                    </Badge>
                    <Badge color="success" className="px-3 py-2">
                      ✓ Preview
                    </Badge>
                  </div>
                </div>
                <div className="alert alert-info mt-3 mb-0">
                  <small>
                    💡 <strong>Lưu ý:</strong> Hình ảnh này sẽ được sử dụng làm nền cho màn hình minigame. 
                    Đảm bảo hình ảnh có độ phân giải cao và không quá rối mắt.
                  </small>
                </div>
              </div>
            )}
          </ModalBody>
          <ModalFooter className="bg-light">
            <Button color="secondary" onClick={handleCloseModal} disabled={loading}>
              <X size={16} className="me-2" />
              Hủy bỏ
            </Button>
            <Button 
              color="primary" 
              onClick={handleSubmit} 
              disabled={loading || (!selectedFile && !editMode)}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Đang xử lý...
                </>
              ) : (
                <>
                  {editMode ? <Edit size={16} className="me-2" /> : <PlusCircle size={16} className="me-2" />}
                  {editMode ? '💾 Cập nhật' : '➕ Thêm mới'}
                </>
              )}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>

      {/* Custom CSS */}
      <style>{`
        .hover-lift {
          transition: all 0.3s ease;
        }
        .hover-lift:hover {
          transform: translateY(-5px);
          box-shadow: 0 1rem 3rem rgba(0,0,0,.175) !important;
        }
        .hover-zoom {
          transition: transform 0.5s ease;
        }
        .hover-lift:hover .hover-zoom {
          transform: scale(1.05);
        }
        .bg-gradient-primary {
          background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
        }
      `}</style>
    </Fragment>
  );
};

export default ImageBackgroundManagement;

